import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Alert,
    Stack,
    TextField,
    TablePagination,
    Snackbar,
    Link,
    IconButton,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    CloudUpload as UploadIcon,
    PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { Title, useGetIdentity } from 'react-admin';
import { supabase } from '../providers/supabase/supabase';

type HailManualRow = {
    id: number;
    aip: string;
    state: string;
    file_path: string;
    file_url: string;
    file_name: string;
    created_at: string;
};

type SortField = 'aip' | 'state';
type SortDir = 'asc' | 'desc';

export const HailManualsPage = () => {
    const { identity } = useGetIdentity();
    const isAdmin = !!(identity as any)?.administrator;

    const [rows, setRows] = useState<HailManualRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [addOpen, setAddOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [sortField, setSortField] = useState<SortField>('aip');
    const [sortDir, setSortDir] = useState<SortDir>('asc');
    const [snackMsg, setSnackMsg] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const PAGE_SIZE = 1000;
            let allData: HailManualRow[] = [];
            let from = 0;
            let hasMore = true;

            while (hasMore) {
                const { data, error: fetchError } = await supabase
                    .from('hail_manuals')
                    .select('*')
                    .order('aip', { ascending: true })
                    .order('state', { ascending: true })
                    .range(from, from + PAGE_SIZE - 1);

                if (fetchError) throw fetchError;
                const page = (data as HailManualRow[]) ?? [];
                allData = allData.concat(page);
                hasMore = page.length === PAGE_SIZE;
                from += PAGE_SIZE;
            }

            setRows(allData);
        } catch (err: any) {
            console.error('Failed to fetch hail manuals:', err);
            setError('Failed to load hail manuals data.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const displayRows = useMemo(() => {
        return [...rows].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];

            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;

            const cmp = String(aVal).localeCompare(String(bVal));
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [rows, sortField, sortDir]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDir('asc');
        }
    };

    const handleDelete = async (row: HailManualRow) => {
        if (
            !window.confirm(
                `Delete manual "${row.file_name}" for ${row.aip} - ${row.state}?`
            )
        )
            return;

        try {
            // Delete file from storage
            const { error: storageErr } = await supabase.storage
                .from('hail-manuals')
                .remove([row.file_path]);
            if (storageErr) console.error('Storage delete error:', storageErr);

            // Delete row from table
            const { error: delErr } = await supabase
                .from('hail_manuals')
                .delete()
                .eq('id', row.id);
            if (delErr) throw delErr;

            setRows(prev => prev.filter(r => r.id !== row.id));
            setSnackMsg('Manual deleted');
        } catch (err: any) {
            console.error('Failed to delete manual:', err);
            setError('Failed to delete manual.');
        }
    };

    const columns: { field: SortField; label: string }[] = [
        { field: 'aip', label: 'AIP' },
        { field: 'state', label: 'State' },
    ];

    return (
        <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
            <Title title="Manuals" />

            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
            >
                <Typography variant="h5" fontWeight={600}>
                    Manuals
                </Typography>
                {isAdmin && (
                    <Tooltip title="Add manual">
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setAddOpen(true)}
                        >
                            Add Manual
                        </Button>
                    </Tooltip>
                )}
            </Stack>

            {rows.length > 0 && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                >
                    {displayRows.length} record
                    {displayRows.length !== 1 ? 's' : ''}
                </Typography>
            )}

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    onClose={() => setError('')}
                >
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        p: 6,
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : rows.length === 0 ? (
                <Paper
                    sx={{
                        p: 6,
                        textAlign: 'center',
                        color: 'text.secondary',
                    }}
                >
                    <PdfIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6" gutterBottom>
                        No hail manuals yet
                    </Typography>
                    {isAdmin ? (
                        <>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Click &quot;Add Manual&quot; to upload a PDF.
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setAddOpen(true)}
                            >
                                Add Manual
                            </Button>
                        </>
                    ) : (
                        <Typography variant="body2">
                            An admin has not uploaded any manuals yet.
                        </Typography>
                    )}
                </Paper>
            ) : (
                <Paper>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {columns.map(col => (
                                        <TableCell
                                            key={col.field}
                                            sx={{ fontWeight: 700 }}
                                            sortDirection={
                                                sortField === col.field
                                                    ? sortDir
                                                    : false
                                            }
                                        >
                                            <TableSortLabel
                                                active={sortField === col.field}
                                                direction={
                                                    sortField === col.field
                                                        ? sortDir
                                                        : 'asc'
                                                }
                                                onClick={() =>
                                                    handleSort(col.field)
                                                }
                                            >
                                                {col.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                    <TableCell sx={{ fontWeight: 700 }}>
                                        Manual
                                    </TableCell>
                                    {isAdmin && (
                                        <TableCell
                                            sx={{
                                                fontWeight: 700,
                                                width: 60,
                                            }}
                                        />
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayRows
                                    .slice(
                                        page * rowsPerPage,
                                        page * rowsPerPage + rowsPerPage
                                    )
                                    .map(row => (
                                        <TableRow key={row.id} hover>
                                            <TableCell>
                                                {row.aip || '\u2014'}
                                            </TableCell>
                                            <TableCell>
                                                {row.state || '\u2014'}
                                            </TableCell>
                                            <TableCell>
                                                <Link
                                                    href={row.file_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    variant="body2"
                                                >
                                                    View
                                                </Link>
                                            </TableCell>
                                            {isAdmin && (
                                                <TableCell>
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() =>
                                                                handleDelete(
                                                                    row
                                                                )
                                                            }
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={displayRows.length}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            )}

            <AddManualDialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onComplete={() => {
                    setAddOpen(false);
                    fetchData();
                }}
            />

            <Snackbar
                open={!!snackMsg}
                autoHideDuration={2000}
                onClose={() => setSnackMsg('')}
                message={snackMsg}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
            />
        </Box>
    );
};

// ---------- Add Manual Dialog ----------

function AddManualDialog({
    open,
    onClose,
    onComplete,
}: {
    open: boolean;
    onClose: () => void;
    onComplete: () => void;
}) {
    const [aip, setAip] = useState('');
    const [state, setState] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: (accepted: File[]) => {
            if (accepted.length > 0) {
                setFile(accepted[0]);
            }
        },
        accept: {
            'application/pdf': ['.pdf'],
        },
        multiple: false,
        disabled: saving,
    });

    const handleClose = () => {
        setAip('');
        setState('');
        setFile(null);
        setError('');
        onClose();
    };

    const handleSave = async () => {
        if (!aip.trim()) {
            setError('AIP is required.');
            return;
        }
        if (!state.trim()) {
            setError('State is required.');
            return;
        }
        if (!file) {
            setError('Please select a PDF file.');
            return;
        }

        setSaving(true);
        setError('');

        try {
            // Upload PDF to storage
            const fileName = `${Date.now()}_${file.name}`;
            const { error: uploadError } = await supabase.storage
                .from('hail-manuals')
                .upload(fileName, file, {
                    contentType: 'application/pdf',
                });

            if (uploadError) {
                throw uploadError;
            }

            const {
                data: { publicUrl },
            } = supabase.storage.from('hail-manuals').getPublicUrl(fileName);

            // Insert row
            const { error: insertError } = await supabase
                .from('hail_manuals')
                .insert({
                    aip: aip.trim(),
                    state: state.trim(),
                    file_path: fileName,
                    file_url: publicUrl,
                    file_name: file.name,
                });

            if (insertError) throw insertError;

            setAip('');
            setState('');
            setFile(null);
            onComplete();
        } catch (err: any) {
            console.error('Failed to save manual:', err);
            setError(err.message || 'Failed to save manual.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add Hail Manual</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}

                    <TextField
                        label="AIP"
                        value={aip}
                        onChange={e => setAip(e.target.value)}
                        fullWidth
                        required
                        disabled={saving}
                    />

                    <TextField
                        label="State"
                        value={state}
                        onChange={e => setState(e.target.value)}
                        fullWidth
                        required
                        disabled={saving}
                    />

                    <Box
                        {...getRootProps()}
                        sx={{
                            border: '2px dashed',
                            borderColor: isDragActive
                                ? 'primary.main'
                                : file
                                  ? 'success.main'
                                  : '#ccc',
                            borderRadius: 2,
                            p: 4,
                            textAlign: 'center',
                            cursor: saving ? 'default' : 'pointer',
                            bgcolor: isDragActive
                                ? 'action.hover'
                                : 'transparent',
                            transition: 'all 0.2s',
                        }}
                    >
                        <input {...getInputProps()} />
                        {file ? (
                            <>
                                <PdfIcon
                                    sx={{
                                        fontSize: 40,
                                        color: 'success.main',
                                        mb: 1,
                                    }}
                                />
                                <Typography>{file.name}</Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                    &mdash; Click or drop to replace
                                </Typography>
                            </>
                        ) : (
                            <>
                                <UploadIcon
                                    sx={{
                                        fontSize: 40,
                                        color: 'text.secondary',
                                        mb: 1,
                                    }}
                                />
                                <Typography>
                                    {isDragActive
                                        ? 'Drop the PDF here...'
                                        : 'Drag & drop a PDF here, or click to select'}
                                </Typography>
                            </>
                        )}
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || !aip.trim() || !state.trim() || !file}
                >
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default HailManualsPage;
